import React from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { colors, typography } from '../theme/tokens';

const PairingCodeModal = ({ open, onClose, onSubmit, pairingCode }) => {
  const takeawayList = useSelector((state) => state.auth.takeawayList);
  const [code, setCode] = React.useState(['', '', '', '', '']);
  const [selectedBranch, setSelectedBranch] = React.useState('');
  const [activeStep, setActiveStep] = React.useState(0);
  const inputRefs = React.useRef([]);

  const steps = ['Select Your Branch', 'Register Code'];

  // Extract branches from takeawayList
  const branches = takeawayList && takeawayList.length > 0
    ? takeawayList.map((takeaway) => ({
      id: takeaway.license_key || takeaway.id,
      name: takeaway.name || takeaway.branch_name || takeaway.license_key || 'Unknown Branch',
      value: takeaway.license_key || takeaway.id,
    }))
    : [];

  const handleCodeChange = (index, value) => {
    // Allow alphanumeric characters (a-z, A-Z, 0-9)
    const alphanumeric = value.replace(/[^a-zA-Z0-9]/g, '');
    if (alphanumeric.length > 1) return;

    const newCode = [...code];
    newCode[index] = alphanumeric.toLowerCase();
    setCode(newCode);

    // Auto-focus next input
    if (alphanumeric && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedBranch) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      const fullCode = code.join('');
      if (fullCode.length === 5) {
        onSubmit({
          branch: selectedBranch,
          pairingCode: fullCode,
        });
        handleReset();
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setCode(['', '', '', '', '']);
    setSelectedBranch('');
    setActiveStep(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleReset} maxWidth="sm" fullWidth>
      <DialogTitle>New Screen</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <FormControl fullWidth>
              <InputLabel>Select Your Branch</InputLabel>
              <Select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                label="Select Your Branch"
              >
                {branches.length > 0 ? (
                  branches.map((branch) => (
                    <MenuItem key={branch.id || branch.value} value={branch.value}>
                      {branch.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No branches available</MenuItem>
                )}
              </Select>
            </FormControl>

            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" gutterBottom>
                1. Download the Digi Solutions Player App
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                On your device, open the app store and search for{' '}
                <Typography component="span" color="primary" fontWeight={600}>
                  DigiSlides TV
                </Typography>
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                2. Get Your Pairing Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A unique 5-digit pairing code will automatically appear on your screen.
              </Typography>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center' }}>

            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Enter the Pairing Code
              </Typography>
              {/* OTP Input Boxes */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  // mb: 6,
                }}
              // onPaste={handlePaste}
              >
                {Array(5).fill('').map((_, index) => (
                  <Box
                    key={index}
                    component="input"
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="text"
                    maxLength={1}
                    value={code[index] || ''}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                      const chars = pastedText.slice(0, 5).split('');
                      const newCode = [...code];
                      chars.forEach((char, i) => {
                        if (index + i < 5) {
                          newCode[index + i] = char;
                        }
                      });
                      setCode(newCode);
                      // Focus the next empty input or the last input
                      const nextEmptyIndex = newCode.findIndex((val, i) => i >= index && !val);
                      if (nextEmptyIndex !== -1) {
                        inputRefs.current[nextEmptyIndex]?.focus();
                      } else {
                        inputRefs.current[4]?.focus();
                      }
                    }}
                    // disabled={isVerifying}
                    sx={{
                      width: { xs: 50, sm: 70 },
                      height: { xs: 50, sm: 70 },
                      fontSize: '1.75rem',
                      fontWeight: typography.fontWeightBold,
                      textAlign: 'center',
                      border: `2px solid ${code[index] ? '#3D3C4A' : '#EFEFF0'}`,
                      borderRadius: 1.5,
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      bgcolor: '#FDFDFD',
                      color: colors.neutral.gray900,
                      '&:focus': {
                        borderColor: '#EFEFF0',
                        border: `1px solid '#EFEFF0'`,
                        boxShadow: `0 0 0 4px rgba(5, 5, 7, 0.20)`,
                      },
                      '&:disabled': {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleReset}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            (activeStep === 0 && !selectedBranch) ||
            (activeStep === 1 && !pairingCode && code.join('').length !== 5)
          }
        >
          {activeStep === steps.length - 1 ? 'Pair Screen' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PairingCodeModal;

